/**
 * drawingImage.js — Fix Round 2026-07-10 (ONE shared drawing-image ladder).
 *
 * Every surface that renders a drawing thumbnail/preview must use these
 * helpers instead of hand-rolling its own URL construction. Prior state:
 * 8+ surfaces each with a different `<img src>` shape (some prefixed
 * BACKEND_URL to already-absolute URLs, some ignored the lazy endpoint,
 * some couldn't detect a PDF that had a query string).
 *
 * === Public API ===
 *   drawingImageProps(drawing, { size } = {})
 *     → { src, onError, onLoad }  — spread onto <img>.
 *       Ladder walked by onError:
 *         (1) drawing.thumbnail_url  (absolute or /api-relative, PDF-safe)
 *         (2) /api/drawings/{id}/thumbnail-lazy  (server-side generator)
 *         (3) hides the <img>; caller shows a FileText icon
 *
 *   isPdfUrl(url) — robust PDF detector (handles ?raw=1, ?dl=0, fragments)
 *
 *   pickPreviewMode(url) — 'pdf' | 'image' | 'unknown'
 *     Used by the drawing viewer to decide iframe vs <img>. If unknown
 *     (e.g. some Dropbox share URL that has no extension exposed), the
 *     caller should default to `iframe` — a PDF loaded into <img> is
 *     the "broken image" symptom the fix targets.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Robust PDF detector.
 *   endsWith('.pdf') is unsafe — Dropbox share URLs come back as
 *   `.../file.pdf?dl=0` or `.../file.pdf?raw=1`, and query-stripped
 *   URLs can carry a `#page=2` fragment.
 */
export function isPdfUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const clean = url.split('#')[0].split('?')[0].toLowerCase();
  return clean.endsWith('.pdf');
}

/** Prefix a raw /api path with BACKEND_URL; leave absolute URLs alone. */
function _resolve(url) {
  if (!url) return null;
  if (typeof url !== 'string') return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url; // relative-with-no-slash — legacy; leave as-is
}

/**
 * Build the props to spread onto an <img> tag rendering a drawing
 * thumbnail. Walks the ladder via onError. The caller is expected to
 * render a fallback icon in a sibling element that we `unhide` after
 * the final failure (via the `data-drawing-image-failed` attr).
 *
 * Options:
 *   size:   ignored today, reserved for future w/h hints
 *   testId: sets data-testid on the img
 */
export function drawingImageProps(drawing, { testId } = {}) {
  if (!drawing || typeof drawing !== 'object') return { src: null };

  // Reject `.pdf` file URLs as an image src — that renders "broken image"
  // (this is exactly the Shelton viewer bug's origin when the ladder
  // silently accepts a PDF url). We accept `thumbnail_url` only if it's
  // not a PDF; otherwise we jump directly to the lazy endpoint which
  // always emits a JPEG.
  const rawThumb = drawing.thumbnail_url;
  const startThumb = rawThumb && !isPdfUrl(rawThumb) ? _resolve(rawThumb) : null;

  const lazyUrl = drawing.id ? `${BACKEND_URL}/api/drawings/${drawing.id}/thumbnail-lazy` : null;
  // Initial src prefers the stored thumbnail_url. If none, we go
  // straight to the lazy endpoint. If neither is available we hand
  // back src=null and the caller renders the icon.
  const initial = startThumb || lazyUrl;

  return {
    src: initial,
    // Attach a data-src-step so debug tooling can see which rung of the
    // ladder is being tried when a card is blank.
    'data-src-step': startThumb ? 'thumb' : (lazyUrl ? 'lazy' : 'none'),
    'data-testid': testId,
    onError: (e) => {
      const t = e.target;
      const step = t.dataset.srcStep || t.getAttribute('data-src-step');
      if (step === 'thumb' && lazyUrl) {
        t.dataset.srcStep = 'lazy';
        t.setAttribute('data-src-step', 'lazy');
        t.src = lazyUrl;
        return;
      }
      // Terminal — hide the img so the sibling icon can appear.
      t.dataset.srcStep = 'failed';
      t.setAttribute('data-drawing-image-failed', '1');
      t.style.display = 'none';
    },
  };
}

/**
 * Preview-pane decision: PDF (iframe) vs image (img).
 * If unknown, default to `pdf` because iframe can render images too
 * (browsers happily display an image inside an iframe) but <img> cannot
 * render a PDF — that's the "broken image" alt symptom.
 */
export function pickPreviewMode(url) {
  if (isPdfUrl(url)) return 'pdf';
  if (!url) return 'unknown';
  const clean = url.split('#')[0].split('?')[0].toLowerCase();
  if (/\.(png|jpe?g|gif|webp|svg|bmp)$/.test(clean)) return 'image';
  return 'unknown';
}
